import { useMemo, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { bookings } from '@repletoryreach/sdk-api';
import { Button, Card, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState, PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { SegmentedControl, Select, Switch } from '@repletoryreach/sdk-ui/inputs';
import { HelpButton } from '../../app/guides/HelpButton';
import { useT } from '../../i18n/I18nProvider';
import { usePersistedState } from '../../lib/usePersistedState';
import { BookingDrawer } from './components/BookingDrawer';
import { NewBookingModal, type NewBookingPrefill } from './components/NewBookingModal';
import {
  BOOKINGS_KEY, bookingErrorText, serviceColour, statusLabel, useBookingSettings, useDateLocale, useOrgTimezone,
  useServices, useStaff
} from './lib/data';
import {
  addDays, daysFrom, formatLocalDate, hhmm, layoutDay, localDateOf, localTimeOf, minutesOfDay, rangeIso,
  snapMinutes, startOfWeek, todayIn, visibleHours
} from './lib/time';
import './bookings.css';

/** Height of one hour on the grid. */
const HOUR_PX = 56;
const PX_PER_MIN = HOUR_PX / 60;
const MAX_PAGES = 5;

type View = 'week' | 'day';

/** Every booking in a window, following the cursor a few pages at most. */
async function loadWindow(from: string, to: string, staffId: string): Promise<bookings.Booking[]> {
  const out: bookings.Booking[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < MAX_PAGES; i++) {
    const page = await bookings.listBookings({ from, to, staff_id: staffId || undefined, limit: 200, cursor });
    out.push(...page.data);
    cursor = page.meta?.next_cursor ?? undefined;
    if (!cursor) break;
  }
  return out;
}

export default function BookingsCalendarPage() {
  const t = useT();
  const nav = useNavigate();
  const tz = useOrgTimezone();
  const locale = useDateLocale();
  const today = todayIn(tz);
  const [view, setView] = usePersistedState<View>('tenant.bookings.view', 'week');
  const [staffId, setStaffId] = usePersistedState('tenant.bookings.staff', '');
  const [showCancelled, setShowCancelled] = usePersistedState('tenant.bookings.showCancelled', false);
  const [anchor, setAnchor] = useState(today);
  const [openId, setOpenId] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<NewBookingPrefill | null>(null);

  const settingsQ = useBookingSettings();
  const servicesQ = useServices(true);
  const staffQ = useStaff();
  const services = servicesQ.data?.data ?? [];
  const staff = staffQ.data?.data ?? [];
  const step = settingsQ.data?.data.slot_step_minutes || 15;

  const first = view === 'week' ? startOfWeek(anchor) : anchor;
  const days = daysFrom(first, view === 'week' ? 7 : 1);
  const range = rangeIso(first, addDays(first, days.length), tz);

  const q = useQuery({
    queryKey: [BOOKINGS_KEY, 'calendar', range.from, range.to, staffId],
    queryFn: () => loadWindow(range.from, range.to, staffId),
    placeholderData: (prev) => prev,
    refetchInterval: 60_000
  });

  const colourOf = useMemo(() => new Map(services.map((s) => [s.id, serviceColour(s.colour)])), [services]);

  const items = useMemo(() => (q.data ?? [])
    .filter((b) => showCancelled || (b.status !== 'cancelled' && b.status !== 'declined'))
    .map((b) => ({
      id: b.id,
      date: localDateOf(b.starts_at, tz),
      start: minutesOfDay(b.starts_at, tz),
      // A booking running past midnight is drawn to the end of its first day.
      end: localDateOf(b.ends_at, tz) === localDateOf(b.starts_at, tz) ? minutesOfDay(b.ends_at, tz) : 24 * 60,
      booking: b
    })), [q.data, showCancelled, tz]);

  const hours = visibleHours(items);
  const gridHeight = ((hours.to - hours.from) / 60) * HOUR_PX;
  const hourMarks = Array.from({ length: (hours.to - hours.from) / 60 }, (_, i) => hours.from + i * 60);

  const label = view === 'week'
    ? t('bookings.calendar.weekOf', { from: formatLocalDate(days[0]!, locale, { day: 'numeric', month: 'short' }), to: formatLocalDate(days[6]!, locale, { day: 'numeric', month: 'short', year: 'numeric' }) })
    : formatLocalDate(anchor, locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  function onEmptyClick(e: MouseEvent<HTMLDivElement>, date: string) {
    if (e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mins = snapMinutes(e.clientY - rect.top, PX_PER_MIN, hours.from, step);
    setPrefill({ date, time: hhmm(mins), staffId: staffId || undefined });
  }

  const noServices = servicesQ.isSuccess && services.length === 0;

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('bookings.calendar.title')}
        description={t('bookings.calendar.description')}
        withDivider={false}
        actions={(
          <Group gap="sm">
            <HelpButton guide="bookings" />
            <Button size="sm" variant="default" onClick={() => nav('/bookings/list')}>{t('bookings.list.title')}</Button>
            <Button size="sm" data-guide="bookings-new" onClick={() => setPrefill({ date: anchor >= today ? anchor : today, staffId: staffId || undefined })}>{t('bookings.new.title')}</Button>
          </Group>
        )}
      />

      {noServices && (
        <Card withBorder>
          <EmptyState
            title={t('bookings.services.emptyTitle')}
            description={t('bookings.services.emptyHint')}
            action={<Button size="sm" onClick={() => nav('/bookings/services')}>{t('bookings.services.addFirst')}</Button>}
          />
        </Card>
      )}

      <Card withBorder data-guide="bookings-calendar-grid">
        <Stack gap="md">
          <Group gap="sm" justify="space-between" wrap="wrap">
            <Group gap="xs" wrap="wrap">
              <Button size="xs" variant="default" onClick={() => setAnchor(today)}>{t('bookings.calendar.today')}</Button>
              <Button size="xs" variant="default" aria-label={t('bookings.calendar.previous')} onClick={() => setAnchor(addDays(anchor, view === 'week' ? -7 : -1))}>‹</Button>
              <Button size="xs" variant="default" aria-label={t('bookings.calendar.next')} onClick={() => setAnchor(addDays(anchor, view === 'week' ? 7 : 1))}>›</Button>
              <Text role="subtitle" aria-live="polite">{label}</Text>
            </Group>
            <Group gap="sm" wrap="wrap" align="center">
              <Select
                size="sm"
                aria-label={t('bookings.field.person')}
                value={staffId}
                onChange={(e) => setStaffId(e.currentTarget.value)}
                data={[{ value: '', label: t('bookings.calendar.everyone') }, ...staff.map((p) => ({ value: p.id, label: p.name }))]}
              />
              <Switch size="sm" label={t('bookings.calendar.showCancelled')} checked={showCancelled} onChange={setShowCancelled} />
              <SegmentedControl
                size="sm"
                value={view}
                onChange={(v) => setView(v as View)}
                data={[{ value: 'week', label: t('bookings.calendar.week') }, { value: 'day', label: t('bookings.calendar.day') }]}
              />
            </Group>
          </Group>

          {q.error && (
            <Alert color="error" role="alert">
              <Group gap="sm" justify="space-between" wrap="wrap">
                <span>{bookingErrorText(t, q.error)}</span>
                <Button size="xs" variant="default" onClick={() => void q.refetch()}>{t('action.retry')}</Button>
              </Group>
            </Alert>
          )}

          <div className={`rr-cal${q.isFetching ? ' is-loading' : ''}`} data-testid="bookings-calendar">
            <div className="rr-cal__head" style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))` }}>
              <span />
              {days.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`rr-cal__day-head${d === today ? ' is-today' : ''}`}
                  onClick={() => { setAnchor(d); setView('day'); }}
                >
                  <span className="rr-cal__day-name">{formatLocalDate(d, locale, { weekday: 'short' })}</span>
                  <span className="rr-cal__day-num">{formatLocalDate(d, locale, { day: 'numeric' })}</span>
                </button>
              ))}
            </div>
            <div className="rr-cal__body" style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))` }}>
              <div className="rr-cal__hours" style={{ height: gridHeight }}>
                {hourMarks.map((m) => (
                  <span key={m} className="rr-cal__hour" style={{ top: (m - hours.from) * PX_PER_MIN }}>{hhmm(m)}</span>
                ))}
              </div>
              {days.map((d) => {
                const placed = layoutDay(items.filter((i) => i.date === d));
                return (
                  <div
                    key={d}
                    className={`rr-cal__col${d === today ? ' is-today' : ''}`}
                    style={{ height: gridHeight, backgroundSize: `100% ${HOUR_PX}px` }}
                    onClick={(e) => onEmptyClick(e, d)}
                    role="presentation"
                    title={t('bookings.calendar.clickToBook')}
                  >
                    {placed.map((p) => {
                      const b = p.booking;
                      const colour = colourOf.get(b.service.id) ?? serviceColour(null);
                      const top = (p.start - hours.from) * PX_PER_MIN;
                      const height = Math.max(18, (p.end - p.start) * PX_PER_MIN - 2);
                      return (
                        <button
                          key={b.id}
                          type="button"
                          className={`rr-cal__block is-${b.status}`}
                          style={{
                            top,
                            height,
                            left: `calc(${(p.column / p.columns) * 100}% + 2px)`,
                            width: `calc(${100 / p.columns}% - 4px)`,
                            ['--rr-cal-colour' as string]: colour
                          }}
                          onClick={(e) => { e.stopPropagation(); setOpenId(b.id); }}
                          aria-label={t('bookings.calendar.blockLabel', {
                            time: localTimeOf(b.starts_at, tz),
                            service: b.service.name,
                            client: b.contact.name ?? '',
                            status: statusLabel(t, b.status)
                          })}
                        >
                          <span className="rr-cal__block-time">{localTimeOf(b.starts_at, tz)}</span>
                          <span className="rr-cal__block-title">{b.contact.name || b.number}</span>
                          {height > 40 && <span className="rr-cal__block-sub">{b.service.name}{b.staff ? ` · ${b.staff.name}` : ''}</span>}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
          <Group gap="md" wrap="wrap">
            <Text role="caption" c="var(--rr-color-text-muted)">{t('bookings.calendar.zone', { zone: tz })}</Text>
            <span className="rr-cal__legend is-requested">{t('bookings.status.requested')}</span>
            <span className="rr-cal__legend is-confirmed">{t('bookings.status.confirmed')}</span>
            {!q.isPending && items.length === 0 && !q.error && (
              <Text role="caption" c="var(--rr-color-text-muted)">{t('bookings.calendar.empty')}</Text>
            )}
          </Group>
        </Stack>
      </Card>

      <BookingDrawer bookingId={openId} onClose={() => setOpenId(null)} tz={tz} />
      <NewBookingModal
        opened={prefill !== null}
        prefill={prefill ?? undefined}
        onClose={() => setPrefill(null)}
        tz={tz}
      />
    </Stack>
  );
}
